import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { Icon } from './Icon'
import type { IconName } from './Icon'
import { ErrorBoundary } from './ErrorBoundary'
import { Link } from 'react-router-dom'
import { useConfig } from '@/lib/ConfigProvider'
import { useSession } from '@/auth/SessionProvider'
import { useOnboarding } from '@/onboarding/OnboardingProvider'
import './AppShell.css'

/**
 * The persistent left rail and the page frame.
 *
 * The design's rail carries seven destinations in a fixed order. Only Overview
 * is built (see issue scope); the rest route to a placeholder that says so
 * rather than to a blank page, because a rail item that goes nowhere silently
 * is indistinguishable from a broken one.
 */
/**
 * Says why the wallet will refuse things, before it refuses them.
 *
 * A customer who is signed in but not yet accepted can reach these screens —
 * this app routes on onboarding but deliberately does not enforce it, because
 * enforcement is the backend's (invariants 7 and 8) and a client-side block
 * would strand anyone whose state we cannot read. The cost of that choice is
 * that the wallet looks usable and is not, so it has to say so.
 */
function OnboardingBanner() {
  const { state } = useOnboarding()

  if (state.status === 'loading') return null
  if (state.status === 'ready' && state.onboarding.state === 'accepted') return null

  return (
    <p className="page__banner" role="status">
      <span>
        {state.status === 'unavailable'
          ? 'We cannot confirm your account is verified, so payments may be refused.'
          : state.status === 'unregistered'
            ? // Not "your identity is not verified": nothing has been submitted
              // to verify. Naming the wrong outstanding step sends someone to
              // look for a document upload that is not the next thing they need.
              'Your wallet is not open yet — we still need your country and legal name.'
            : 'Your identity is not verified yet, so payments will be refused.'}
      </span>
      <Link to="/welcome">Finish setting up</Link>
    </p>
  )
}

const NAV: readonly { to: string; label: string; icon: IconName }[] = [
  { to: '/', label: 'Overview', icon: 'Home2' },
  { to: '/balances', label: 'Balances', icon: 'Wallet' },
  { to: '/payments', label: 'Payments', icon: 'Transfer' },
  { to: '/cards', label: 'Cards', icon: 'Card3' },
  { to: '/people', label: 'People', icon: 'Recipients' },
  { to: '/earnings', label: 'Earnings', icon: 'Chart' },
]

export function AppShell() {
  const config = useConfig()
  const { state, logout } = useSession()
  // The router's location, not the DOM global — `origin.ts` is the only file
  // permitted to read `location` and this is not it (§8). It is also the correct
  // source: the global is not reactive, so the boundary below would reset on
  // whatever else happened to re-render rather than on navigation.
  const location = useLocation()

  // AppShell only ever renders behind RequireSession, so this is always the
  // authenticated case. Read defensively anyway rather than asserting: a future
  // route that mounts the shell without the gate should degrade to a blank
  // identity, not throw inside a layout component.
  const identity = state.status === 'authenticated' ? state.identity : null

  return (
    <div className="shell">
      <nav className="rail" aria-label="Primary">
        <div className="rail__top">
          <span className="rail__wordmark">TESORA</span>
          <ul className="rail__nav">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} end={item.to === '/'} className="rail__item">
                  <Icon name={item.icon} size={20} />
                  <span>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>

        <div className="rail__bottom">
          {/*
            THE USER BLOCK IS THE WAY INTO SETTINGS.

            Settings used to be the seventh rail item. It is not a destination
            in the sense the other six are — they are places money lives, and
            this is where you go to change how the account behaves — so it sits
            on the person instead, which is where somebody looks for it.

            The sign-out control stays a SIBLING of the link, not a child of it.
            Nesting a button inside an anchor is invalid, and it would make the
            most destructive control on the rail share a hit area with the most
            routine one.
          */}
          <div className="rail__user">
            <NavLink to="/settings" className="rail__userLink" title="Settings">
            <span className="rail__avatar" aria-hidden="true">
              {identity?.initials ?? '··'}
            </span>
            <span className="rail__userText">
              <span className="rail__userName" title={identity?.label ?? ''}>
                {identity?.label ?? 'Signed in'}
              </span>
              {/*
                Deliberately NOT a verification tier or an entitlement. Ory
                reports the assurance level the *session* reached; it says
                nothing about what this customer may do. Rendering "verified"
                here as though it were a KYC state would put an authorization
                claim in a frontend, which §6 and invariant 8 both forbid.
              */}
              <span className="rail__userStatus">
                {identity?.verified === true ? 'Two-factor session' : 'Signed in'}
              </span>
            </span>
            </NavLink>
            <button
              type="button"
              className="rail__signout"
              onClick={() => void logout()}
              title="Sign out"
              aria-label="Sign out"
            >
              ⏻
            </button>
          </div>

          {/*
            The environment banner is not decoration. Backend CLAUDE.md §5b:
            "Staging carries no production data." An operator who cannot tell at
            a glance which environment they are looking at is one click from
            treating a sandbox figure as real.
          */}
          {config.environment !== 'production' && (
            <p className="rail__env" role="status">
              <strong>{config.environment}</strong>
              <span>{config.release}</span>
            </p>
          )}
        </div>
      </nav>

      <main className="page">
        <OnboardingBanner />
        {/*
          A failing screen must not take the rail with it (#95).

          Without this the app-level boundary catches it and the customer loses
          the navigation and the sign-out button too — which is a materially
          worse position than losing one panel, and an avoidable one since the
          rail does not depend on anything the panel is doing.

          `key` is the whole reset mechanism. A boundary that has caught stays
          caught until its state is cleared, so without remounting on navigation
          a customer who hit an error on /balances would still be looking at it
          after clicking Payments — an error message that outlives the screen it
          was about. Remounting per path is cheaper than plumbing a reset through
          the router, and it cannot get out of step with the location.
        */}
        <ErrorBoundary scope="panel" key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  )
}
